<!--
  Provision dashboard, start page (04-frontend.md §3, KPI catalog 01 §6).

  One WIDE tile per provision type with its most recent run - build shape of the bm precedent
  (bm-billing-start.component.html): status first, numbers below, the whole tile is the link
  into the run.

  Order WITHIN the tile is a business decision: risk (K-09...K-12) sits BEFORE the plain run
  numbers (01 §6). The progress bar counts along CP_RUN_STATUS_ORDER, never by enum ordinal
  (04 §5.2).

  DEVIATION from the bm reference: bm-billing-start.component.html carries icon size, progress
  track height and progress fill colour as style="..." attributes. Here those are PrimeFlex
  utilities (w-2rem h-2rem, hover:shadow-3, bg-*-500) and the cp--prefixed class
  .cp-kpi-progress-track instead. Inline styles and px values are forbidden by
  fe_core_ux-guidelines (layout and colour rules), so the bm file is not a pattern to copy on that
  point. Structure, ordering and interaction of the tile stay exactly the reference's.
-->
<div class="cp-provision-dashboard-start pt-2" [attr.data-widget-alias]="kpiAlias">

    @if (initialized) {

        <!-- While a cp job is running, the numbers are an interim state. The component reloads
             itself on the running -> not-running transition (04 §4); the banner only explains why
             the numbers are about to change. -->
        @if (jobRunning) {
            <div class="flex align-items-center gap-2 mb-3 p-3 border-round-xl bg-orange-100 text-orange-800">
                <i class="fa-solid fa-spinner fa-spin"></i>
                <span class="text-sm">
                    Hintergrundprozesse laufen – die Kennzahlen werden nach Abschluss automatisch aktualisiert.
                </span>
            </div>
        }

        @if (kpisLoading) {
            <div class="flex justify-content-center p-6">
                <p-progressSpinner strokeWidth="4"></p-progressSpinner>
            </div>
        } @else if (loadFailed) {
            <div class="surface-card border-round-xl border-1 surface-border p-6 text-center">
                <i class="fa-solid fa-triangle-exclamation text-4xl text-red-400 mb-3"></i>
                <p class="text-500 m-0 mb-3">Die Provisionskennzahlen konnten nicht geladen werden.</p>
                <p-button label="Erneut laden"
                          icon="fa-solid fa-rotate-right"
                          size="small"
                          [outlined]="true"
                          (onClick)="loadKpis()">
                </p-button>
            </div>
        } @else if (provisionTypeKpis.length === 0) {
            <div class="surface-card border-round-xl border-1 surface-border p-6 text-center">
                <i class="fa-solid fa-percent text-4xl text-300 mb-3"></i>
                <p class="text-500 m-0 mb-3">Es ist noch kein Provisionstyp angelegt. Ohne
                    Provisionstyp gibt es keine Kennzahlen und keinen Provisionslauf.</p>
                <p-button label="Provisionstyp anlegen"
                          icon="fa-solid fa-folder-tree"
                          size="small"
                          [outlined]="true"
                          (onClick)="navigateToProvisionTypeConfig()">
                </p-button>
            </div>
        } @else {

            <div class="grid">
                @for (typeKpi of provisionTypeKpis; track typeKpi.provisionTypeId) {
                    <div class="col-12 xl:col-6">

                        @if (typeKpi.latestProvisionRun; as latest) {
                            <div class="cp-kpi-card surface-card border-round-xl shadow-1 hover:shadow-3 border-1 surface-border h-full flex flex-column cursor-pointer transition-colors transition-duration-150 hover:surface-50"
                                 (click)="navigateToProvisionRun(latest.provisionRunId)">

                                <!-- Header: provision type, run, period -->
                                <div class="flex align-items-center gap-3 p-3 border-bottom-1 surface-border">
                                    <span class="inline-flex align-items-center justify-content-center border-round bg-primary-100 text-primary-800 w-2rem h-2rem">
                                        <i class="fa-solid fa-percent"></i>
                                    </span>

                                    <div class="flex-1 min-w-0">
                                        <div class="font-semibold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                            {{ typeKpi.provisionTypeName }}
                                        </div>
                                        <div class="text-xs text-500 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                            {{ latest.provisionRunName }}
                                            @if (latest.periodStartDate && latest.periodEndDate) {
                                                · {{ latest.periodStartDate | date:'dd.MM.yyyy' }} – {{ latest.periodEndDate | date:'dd.MM.yyyy' }}
                                            }
                                            @if (latest.valutaDate) {
                                                · Valuta {{ latest.valutaDate | date:'dd.MM.yyyy' }}
                                            }
                                        </div>
                                    </div>

                                    <i class="fa-solid fa-arrow-right text-400"></i>
                                </div>

                                <!-- K-01 run status: chip + progress along the business ordering list -->
                                <div class="px-3 pt-3">
                                    <div class="flex align-items-center justify-content-between gap-2 mb-2">
                                        <span class="inline-flex align-items-center gap-2 px-2 py-1 border-round text-sm font-semibold"
                                              [ngClass]="[presentationOf(latest.provisionRunStatus).background,
                                                          presentationOf(latest.provisionRunStatus).text]">
                                            <i [class]="presentationOf(latest.provisionRunStatus).icon"></i>
                                            {{ presentationOf(latest.provisionRunStatus).label }}
                                        </span>

                                        @if (isRunning(latest.provisionRunStatus)) {
                                            <span class="text-xs text-500">Hintergrundprozess läuft</span>
                                        } @else if (isCompleted(latest.provisionRunStatus)) {
                                            <span class="text-xs text-500">Lauf abgeschlossen</span>
                                        } @else if (isFailed(latest.provisionRunStatus)) {
                                            <span class="text-xs text-red-600">Wiederanlauf im Lauf-Detail</span>
                                        }
                                    </div>

                                    <!--
                                      Track height lives in the cp--prefixed class
                                      .cp-kpi-progress-track (SCSS), the fill colour arrives as a
                                      PrimeFlex colour class from progressBackgroundOf(). Only the
                                      fill WIDTH stays a style binding - a per-run percentage cannot
                                      be expressed as a utility class.
                                    -->
                                    <div class="cp-kpi-progress-track surface-200 border-round overflow-hidden">
                                        <div class="h-full border-round"
                                             [ngClass]="progressBackgroundOf(latest.provisionRunStatus)"
                                             [style.width.%]="progressOf(latest.provisionRunStatus)"></div>
                                    </div>

                                    @if (isAborted(latest.provisionRunStatus)) {
                                        <div class="flex align-items-center gap-2 mt-2 text-xs text-600">
                                            <i class="fa-solid fa-ban"></i>
                                            Der Lauf wurde abgebrochen; berechnete Buchungen wurden verworfen.
                                        </div>
                                    }
                                </div>

                                <!--
                                  RISK (K-09...K-12) - deliberately placed BEFORE the run numbers (01 §6).
                                  Shortfall and payout stop are TWO tiles: an advisor in shortfall
                                  (reserve below open liability) is still paid out; the payout brake
                                  (U-03 / M-012) only skips an advisor whose real reserve balance is
                                  negative. See the class TSDoc.
                                -->
                                <div class="px-3 pt-3">
                                    <div class="flex align-items-center gap-2 mb-2">
                                        <span class="text-xs font-bold uppercase"
                                              [ngClass]="hasRiskSignal(latest) ? 'text-red-600' : 'text-500'">Risiko</span>
                                        @if (hasRiskSignal(latest)) {
                                            <i class="fa-solid fa-triangle-exclamation text-red-500 text-xs"></i>
                                        }
                                    </div>

                                    <div class="grid border-round-lg"
                                         [ngClass]="hasRiskSignal(latest) ? 'bg-red-50' : 'surface-50'">
                                        <div class="col-6 md:col-4 p-3">
                                            <div class="text-xs font-semibold text-500 uppercase mb-1">Haftungsbestand</div>
                                            <div class="font-bold text-900">{{ formatCurrency(latest.liabilityStockAmount) }}</div>
                                        </div>

                                        <div class="col-6 md:col-4 p-3">
                                            <div class="text-xs font-semibold text-500 uppercase mb-1">Deckungsgrad Rücklage</div>
                                            <div class="font-bold"
                                                 [ngClass]="isCoverageCritical(latest) ? 'text-red-700' : 'text-900'">
                                                {{ formatPercent(latest.reserveCoverageQuotePercent) }}
                                            </div>
                                        </div>

                                        <div class="col-6 md:col-4 p-3">
                                            <div class="text-xs font-semibold text-500 uppercase mb-1"
                                                 pTooltip="Berater, deren Rücklage die offene Haftung nicht deckt (Unterdeckung > 0 im Reiter „Deckung“ dieses Laufs). Sie werden trotzdem ausgezahlt."
                                                 tooltipPosition="top">
                                                Berater in Unterdeckung <i class="fa-regular fa-circle-question ml-1"></i>
                                            </div>
                                            <div class="font-bold"
                                                 [ngClass]="latest.agentsInShortfallCount > 0 ? 'text-orange-700' : 'text-900'">
                                                {{ formatNumber(latest.agentsInShortfallCount) }}
                                            </div>
                                            @if (latest.agentsInShortfallCount > 0) {
                                                <div class="text-xs text-500">keine Auszahlungssperre</div>
                                            }
                                        </div>

                                        <div class="col-6 md:col-4 p-3">
                                            <div class="text-xs font-semibold text-500 uppercase mb-1"
                                                 pTooltip="Berater mit negativem Rücklagensaldo (nur aktive Buchungen). Die Auszahlung überspringt sie und meldet M-012. Gezählt über alle Abrechnungskonten, nicht je Provisionsart."
                                                 tooltipPosition="top">
                                                Auszahlung gesperrt <i class="fa-regular fa-circle-question ml-1"></i>
                                            </div>
                                            <div class="font-bold"
                                                 [ngClass]="(agentsWithNegativeReserveBalanceCount ?? 0) > 0 ? 'text-red-700' : 'text-900'">
                                                {{ formatOptionalNumber(agentsWithNegativeReserveBalanceCount) }}
                                            </div>
                                            <div class="text-xs"
                                                 [ngClass]="(agentsWithNegativeReserveBalanceCount ?? 0) > 0 ? 'text-red-600' : 'text-500'">
                                                Rücklage negativ
                                            </div>
                                        </div>

                                        <div class="col-6 md:col-4 p-3">
                                            <div class="text-xs font-semibold text-500 uppercase mb-1">Negativer Provisionssaldo</div>
                                            <div class="font-bold"
                                                 [ngClass]="latest.agentsWithNegativeProvisionBalanceCount > 0 ? 'text-orange-700' : 'text-900'">
                                                {{ formatNumber(latest.agentsWithNegativeProvisionBalanceCount) }}
                                            </div>
                                            @if (latest.agentsWithNegativeProvisionBalanceCount > 0) {
                                                <div class="text-xs text-500">offene Forderung</div>
                                            }
                                        </div>
                                    </div>
                                </div>

                                <!-- Run numbers K-03...K-08 -->
                                <div class="grid p-3 pt-3">
                                    <div class="col-6 md:col-4">
                                        <div class="text-xs font-semibold text-500 uppercase mb-1">Berechnete Provision</div>
                                        <div class="font-bold text-900">{{ formatCurrency(latest.calculatedProvisionAmount) }}</div>
                                        <div class="text-xs text-500">Vorschau</div>
                                    </div>

                                    <div class="col-6 md:col-4">
                                        <div class="text-xs font-semibold text-green-600 uppercase mb-1">Aktivierte Provision</div>
                                        <div class="font-bold text-900">{{ formatCurrency(latest.activeProvisionAmount) }}</div>
                                    </div>

                                    <div class="col-6 md:col-4">
                                        <div class="text-xs font-semibold text-blue-600 uppercase mb-1">Rücklagenzuführung</div>
                                        <div class="font-bold text-900">{{ formatCurrency(latest.reserveContributionAmount) }}</div>
                                    </div>

                                    <div class="col-6 md:col-4">
                                        <div class="text-xs font-semibold text-orange-600 uppercase mb-1">Storno-Rückbelastung</div>
                                        <div class="font-bold text-900">{{ formatCurrency(latest.cancellationChargeAmount) }}</div>
                                        <div class="text-xs text-500">Quote {{ formatPercent(latest.cancellationQuotePercent) }}</div>
                                    </div>

                                    <div class="col-6 md:col-4">
                                        <div class="text-xs font-semibold text-purple-600 uppercase mb-1">Auszahlungsvolumen</div>
                                        <div class="font-bold text-900">{{ formatCurrency(latest.payoutVolumeAmount) }}</div>
                                    </div>

                                    <div class="col-6 md:col-4">
                                        <div class="text-xs font-semibold text-500 uppercase mb-1">Verarbeitete Verträge</div>
                                        <div class="font-bold text-900">{{ formatNumber(contractRunTotalOf(latest)) }}</div>
                                    </div>
                                </div>

                                <!-- K-02 outcome chips, K-13 messages, K-14 data gaps -->
                                <div class="px-3 pb-3 mt-auto flex flex-column gap-2">

                                    <!-- Two rows with a label each: "Fehler" exists both as a contract
                                         outcome and as a message severity, and unlabelled the two
                                         chips read as the same figure twice. -->
                                    <div class="flex flex-wrap align-items-center gap-2">
                                        @if (contractRunTotalOf(latest) > 0) {
                                            <span class="text-xs font-semibold text-500 w-6rem">Verträge</span>
                                        }
                                        @for (contractRunStatus of contractRunStatusOrder; track contractRunStatus) {
                                            @if (contractRunCountOf(latest, contractRunStatus) > 0) {
                                                <span class="inline-flex align-items-center gap-1 px-2 py-1 border-round text-xs font-semibold"
                                                      [ngClass]="[contractRunStatusPresentationOf(contractRunStatus).background,
                                                                  contractRunStatusPresentationOf(contractRunStatus).text]">
                                                    <i [class]="contractRunStatusPresentationOf(contractRunStatus).icon"></i>
                                                    {{ contractRunStatusPresentationOf(contractRunStatus).label }}
                                                    {{ formatNumber(contractRunCountOf(latest, contractRunStatus)) }}
                                                </span>
                                            }
                                        }
                                    </div>

                                    <div class="flex flex-wrap align-items-center gap-2">
                                        @if (messageTotalOf(latest) > 0 || dataGapTotalOf(latest) > 0) {
                                            <span class="text-xs font-semibold text-500 w-6rem">Meldungen</span>
                                        }
                                        @if (messageTotalOf(latest) > 0) {
                                            @for (severity of messageSeverityOrder; track severity) {
                                                @if (messageCountOf(latest, severity) > 0) {
                                                    <span class="inline-flex align-items-center gap-1 px-2 py-1 border-round text-xs font-semibold"
                                                          [ngClass]="[messageSeverityPresentationOf(severity).background,
                                                                      messageSeverityPresentationOf(severity).text]">
                                                        <i [class]="messageSeverityPresentationOf(severity).icon"></i>
                                                        {{ messageSeverityPresentationOf(severity).label }}
                                                        {{ formatNumber(messageCountOf(latest, severity)) }}
                                                    </span>
                                                }
                                            }
                                        }

                                        @if (dataGapTotalOf(latest) > 0) {
                                            <span class="inline-flex align-items-center gap-1 px-2 py-1 border-round text-xs font-semibold bg-yellow-100 text-yellow-800"
                                                  [pTooltip]="'Verträge ohne Berater: ' + formatNumber(latest.contractsWithoutAgentCount)
                                                              + ' · ohne Beitrag: ' + formatNumber(latest.contractsWithoutPriceCount)">
                                                <i class="fa-solid fa-circle-question"></i>
                                                Datenlücken {{ formatNumber(dataGapTotalOf(latest)) }}
                                            </span>
                                        }
                                    </div>
                                </div>
                            </div>

                        } @else {
                            <!-- Configured but never run. Deliberately visible (bm behaviour): a newly
                                 created provision type should show up on the dashboard before it has
                                 its first run - otherwise it looks unconfigured. -->
                            <div class="surface-card border-round-xl border-1 surface-border border-dashed h-full flex align-items-center gap-3 p-3">
                                <span class="inline-flex align-items-center justify-content-center border-round surface-100 text-400 w-2rem h-2rem">
                                    <i class="fa-solid fa-percent"></i>
                                </span>
                                <div class="flex-1 min-w-0">
                                    <div class="font-semibold text-700 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                        {{ typeKpi.provisionTypeName }}
                                    </div>
                                    <div class="text-xs text-500">Noch kein Provisionslauf</div>
                                </div>
                            </div>
                        }
                    </div>
                }
            </div>

            @if (generatedAt) {
                <div class="text-xs text-500 pt-2 pb-4">
                    <i class="fa-solid fa-clock mr-2"></i>
                    Stand {{ generatedAt | date:'dd.MM.yyyy HH:mm' }}
                </div>
            }
        }
    }
</div>
